import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, ArrowsLeftRight, Star } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { inputCls } from "./shared";

const blankLoc = () => ({ code: "", name: "", type: "warehouse", parent_id: "" });
const blankXfer = () => ({ sku: "", from_location_id: "", to_location_id: "", quantity: "", note: "" });

export function Locations({ products, locations, refreshKey, onChange }) {
  const [form, setForm] = useState(blankLoc());
  const [xfer, setXfer] = useState(blankXfer());
  const [levels, setLevels] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setX = (k, v) => setXfer((f) => ({ ...f, [k]: v }));
  const active = useMemo(() => locations.filter((l) => l.active !== false), [locations]);
  const warehouses = useMemo(() => active.filter((l) => l.type === "warehouse"), [active]);
  const destinations = useMemo(() => active.filter((l) => l.id !== xfer.from_location_id), [active, xfer.from_location_id]);

  useEffect(() => {
    api.get("/scm/stock-levels").then((r) => setLevels(r.data.levels)).catch(() => {});
    api.get("/scm/transfers").then((r) => setTransfers(r.data)).catch(() => {});
  }, [refreshKey]);

  const createLoc = async (e) => {
    e.preventDefault();
    try {
      await api.post("/scm/locations", { ...form, parent_id: form.parent_id || null });
      toast.success("Location added");
      setForm(blankLoc());
      onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const patchLoc = async (id, body, msg) => {
    try {
      await api.patch(`/scm/locations/${id}`, body);
      toast.success(msg);
      onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const transfer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/scm/transfers", { ...xfer, quantity: Number(xfer.quantity) });
      toast.success("Stock transferred");
      setXfer(blankXfer());
      onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const fromQty = xfer.sku && xfer.from_location_id ? levels.find((r) => r.sku === xfer.sku)?.by_location?.[xfer.from_location_id] ?? 0 : null;

  return (
    <div data-testid="locations-panel">
      <form onSubmit={createLoc} className="grid grid-cols-2 md:grid-cols-[140px_1fr_140px_200px_auto] gap-px bg-zinc-200 border-b border-zinc-950" data-testid="location-form">
        <input value={form.code} onChange={(e) => setF("code", e.target.value.toUpperCase())} required placeholder="Code (WH-A)" data-testid="loc-code-input" className={`${inputCls} tnum`} />
        <input value={form.name} onChange={(e) => setF("name", e.target.value)} required placeholder="Location name" data-testid="loc-name-input" className={inputCls} />
        <select value={form.type} onChange={(e) => setF("type", e.target.value)} data-testid="loc-type-select" className={inputCls}>
          <option value="warehouse">Warehouse</option>
          <option value="bin">Bin / Rack</option>
        </select>
        <select value={form.parent_id} onChange={(e) => setF("parent_id", e.target.value)} data-testid="loc-parent-select" className={inputCls}>
          <option value="">— no parent —</option>
          {warehouses.map((l) => <option key={l.id} value={l.id}>{`inside ${l.code}`}</option>)}
        </select>
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00] flex items-center justify-center gap-1" data-testid="loc-create-btn"><Plus size={14} /> ADD LOCATION</button>
      </form>

      <div className="px-5 py-3 border-b border-zinc-200 flex flex-wrap gap-2" data-testid="locations-list">
        {locations.map((l) => (
          <div key={l.id} className={`border px-3 py-2 text-xs flex items-center gap-3 ${l.active === false ? "border-zinc-200 text-zinc-400" : "border-zinc-950"}`} data-testid={`loc-chip-${l.code}`}>
            <div>
              <div className="font-mono font-semibold tnum flex items-center gap-1">{l.code}{l.is_default && <Star size={10} weight="fill" className="text-[#FF3E00]" />}</div>
              <div className="text-zinc-500">{l.name} · {l.type}{l.parent_id ? ` in ${locations.find((p) => p.id === l.parent_id)?.code || ""}` : ""}</div>
            </div>
            <div className="flex flex-col gap-1 text-[10px] font-mono">
              {!l.is_default && l.active !== false && <button onClick={() => patchLoc(l.id, { is_default: true }, `${l.code} is now the default`)} className="text-zinc-500 hover:text-zinc-950 hover:underline" data-testid={`loc-default-${l.code}`}>MAKE DEFAULT</button>}
              {!l.is_default && <button onClick={() => patchLoc(l.id, { active: l.active === false }, l.active === false ? "Location re-activated" : "Location deactivated")} className="text-zinc-500 hover:text-[#D0021B] hover:underline" data-testid={`loc-toggle-${l.code}`}>{l.active === false ? "ACTIVATE" : "DEACTIVATE"}</button>}
            </div>
          </div>
        ))}
      </div>

      <div className="px-5 py-3 border-b border-zinc-950 label-sm text-zinc-500">STOCK BY LOCATION</div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">SKU</th>
            <th className="text-left px-4 py-2.5">Product</th>
            {active.map((l) => <th key={l.id} className="text-right px-4 py-2.5 tnum">{l.code}</th>)}
            <th className="text-right px-4 py-2.5">Total</th>
          </tr>
        </thead>
        <tbody data-testid="stock-levels-tbody">
          {levels.map((r) => (
            <tr key={r.sku} className="border-b border-zinc-100" data-testid={`level-row-${r.sku}`}>
              <td className="px-4 py-2 tnum text-xs">{r.sku}</td>
              <td className="px-4 py-2">{r.name}</td>
              {active.map((l) => <td key={l.id} className={`px-4 py-2 text-right tnum ${r.by_location[l.id] ? "" : "text-zinc-300"}`} data-testid={`level-${r.sku}-${l.code}`}>{r.by_location[l.id] || 0}</td>)}
              <td className={`px-4 py-2 text-right tnum font-semibold ${r.located !== r.total ? "text-[#D0021B]" : ""}`} title={r.located !== r.total ? `Located ${r.located} ≠ total ${r.total}` : ""}>{r.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <div className="px-5 py-3 border-y border-zinc-950 label-sm text-zinc-500 flex items-center gap-2"><ArrowsLeftRight size={12} /> TRANSFER STOCK BETWEEN LOCATIONS</div>
      <form onSubmit={transfer} className="grid grid-cols-2 md:grid-cols-[1fr_180px_180px_110px_1fr_auto] gap-px bg-zinc-200 border-b border-zinc-950" data-testid="transfer-form">
        <select value={xfer.sku} onChange={(e) => setX("sku", e.target.value)} required data-testid="xfer-sku-select" className={inputCls}>
          <option value="">— product —</option>
          {products.map((p) => <option key={p.id} value={p.sku}>{`${p.sku} · ${p.name}`}</option>)}
        </select>
        <select value={xfer.from_location_id} onChange={(e) => setX("from_location_id", e.target.value)} required data-testid="xfer-from-select" className={inputCls}>
          <option value="">— from —</option>
          {active.map((l) => <option key={l.id} value={l.id}>{l.code}</option>)}
        </select>
        <select value={xfer.to_location_id} onChange={(e) => setX("to_location_id", e.target.value)} required data-testid="xfer-to-select" className={inputCls}>
          <option value="">— to —</option>
          {destinations.map((l) => <option key={l.id} value={l.id}>{l.code}</option>)}
        </select>
        <input type="number" min="1" step="1" value={xfer.quantity} onChange={(e) => setX("quantity", e.target.value)} required placeholder={fromQty !== null ? `max ${fromQty}` : "Qty"} data-testid="xfer-qty-input" className={`${inputCls} tnum`} />
        <input value={xfer.note} onChange={(e) => setX("note", e.target.value)} placeholder="Note" data-testid="xfer-note-input" className={inputCls} />
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00]" data-testid="xfer-submit-btn">TRANSFER</button>
      </form>
      <table className="w-full text-xs">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-[10px] tracking-wider uppercase text-zinc-500">
            <th className="text-left px-4 py-2">Transfer</th>
            <th className="text-left px-4 py-2">Date</th>
            <th className="text-left px-4 py-2">SKU</th>
            <th className="text-left px-4 py-2">From → To</th>
            <th className="text-right px-4 py-2">Qty</th>
            <th className="text-left px-4 py-2">Note</th>
          </tr>
        </thead>
        <tbody data-testid="transfers-tbody">
          {transfers.length === 0 && <tr><td colSpan={6} className="px-4 py-4 text-zinc-400 font-mono">No transfers yet.</td></tr>}
          {transfers.map((t) => (
            <tr key={t.id} className="border-b border-zinc-100" data-testid={`xfer-row-${t.xfer_no}`}>
              <td className="px-4 py-2 tnum">{t.xfer_no}</td>
              <td className="px-4 py-2 tnum text-zinc-500">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
              <td className="px-4 py-2 tnum">{t.sku} <span className="text-zinc-500">· {t.product_name}</span></td>
              <td className="px-4 py-2 tnum">{t.from_code} → {t.to_code}</td>
              <td className="px-4 py-2 text-right tnum font-semibold">{t.quantity}</td>
              <td className="px-4 py-2 text-zinc-500">{t.note}<div className="text-zinc-400">{t.created_by}</div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
